export interface Ingredient {
  name: string;
  amount: string;
  unit: string;
  base_ingredient: string;
  alternatives: string[];
  category: 'produce' | 'meat' | 'dairy' | 'bread' | 'frozen' | 'canned' | 'pantry-staples' | 'baking' | 'condiments' | 'spices';
}

export interface Recipe {
  id: string;
  title: string;
  ingredients: Ingredient[];
  instructions: string[];
  source_url: string;
  image_url: string | null;
  user_id: string;
  notes?: string;
  rating?: {
    value: number;
    count: number;
    source: string;
  };
  reviews?: {
    text: string;
    author: string;
    rating: number;
    date: string;
  }[];
}

export interface Meal {
  id: string;
  name: string;
  recipeIds: string[];
  notes?: string;
}

export interface GroceryStore {
  id: string;
  name: string;
  searchUrl: string;
  logo: string;
  country: string;
  type: 'supermarket' | 'delivery';
}

export interface UserGroceryStore {
  id: string;
  user_id: string;
  store_id: string;
  store_name: string;
  search_url: string;
  logo_url: string | null;
  country: string;
  is_default: boolean;
  created_at: string;
}

export const INGREDIENT_CATEGORIES = {
  'produce': {
    name: 'Produce',
    description: 'Fresh fruits and vegetables',
    examples: ['apples', 'lettuce', 'tomatoes', 'onions'],
    priority: 1
  },
  'meat': {
    name: 'Meat & Seafood',
    description: 'Fresh and frozen proteins',
    examples: ['chicken', 'beef', 'salmon', 'pork'],
    priority: 2
  },
  'dairy': {
    name: 'Dairy & Eggs',
    description: 'Milk products and eggs',
    examples: ['milk', 'cheese', 'butter', 'eggs'],
    priority: 3
  },
  'bread': {
    name: 'Bread & Bakery',
    description: 'Fresh bread and baked goods',
    examples: ['bread', 'rolls', 'tortillas', 'pita'],
    priority: 4
  },
  'frozen': {
    name: 'Frozen',
    description: 'Frozen ingredients and meals',
    examples: ['frozen peas', 'ice cream', 'frozen pizza'],
    priority: 5
  },
  'canned': {
    name: 'Canned & Jarred',
    description: 'Preserved ingredients',
    examples: ['canned tomatoes', 'beans', 'pickles'],
    priority: 6
  },
  'pantry-staples': {
    name: 'Pantry Staples',
    description: 'Common ingredients you likely have',
    examples: ['salt', 'pepper', 'olive oil', 'flour', 'sugar', 'rice'],
    priority: 7
  },
  'baking': {
    name: 'Baking',
    description: 'Baking ingredients',
    examples: ['baking powder', 'vanilla extract', 'chocolate chips'],
    priority: 8
  },
  'condiments': {
    name: 'Condiments & Sauces',
    description: 'Sauces, spreads, and dressings',
    examples: ['ketchup', 'mayonnaise', 'soy sauce'],
    priority: 9
  },
  'spices': {
    name: 'Spices & Herbs',
    description: 'Dried herbs and spices',
    examples: ['basil', 'cinnamon', 'cumin'],
    priority: 10
  }
} as const;

export const COUNTRIES = {
  'US': 'United States',
  'UK': 'United Kingdom',
  'CA': 'Canada',
  'AU': 'Australia'
} as const;

export const groceryStoresByCountry: Record<keyof typeof COUNTRIES, GroceryStore[]> = {
  'US': [
    // Supermarkets (10)
    {
      id: 'walmart',
      name: 'Walmart',
      searchUrl: 'https://www.walmart.com/search?q=',
      logo: 'https://www.walmart.com/favicon.ico',
      country: 'US',
      type: 'supermarket'
    },
    {
      id: 'kroger',
      name: 'Kroger',
      searchUrl: 'https://www.kroger.com/search?query=',
      logo: 'https://www.kroger.com/favicon.ico',
      country: 'US',
      type: 'supermarket'
    },
    {
      id: 'target',
      name: 'Target',
      searchUrl: 'https://www.target.com/s?searchTerm=',
      logo: 'https://www.target.com/favicon.ico',
      country: 'US',
      type: 'supermarket'
    },
    {
      id: 'wholefoodsmarket',
      name: 'Whole Foods Market',
      searchUrl: 'https://www.wholefoodsmarket.com/search?text=',
      logo: 'https://assets.wholefoodsmarket.com/www/company/whole-foods-market-logo.png',
      country: 'US',
      type: 'supermarket'
    },
    {
      id: 'safeway',
      name: 'Safeway',
      searchUrl: 'https://www.safeway.com/shop/search-results.html?q=',
      logo: 'https://www.safeway.com/content/dam/safeway/images/logos/safeway-logo.svg',
      country: 'US',
      type: 'supermarket'
    },
    {
      id: 'albertsons',
      name: 'Albertsons',
      searchUrl: 'https://www.albertsons.com/shop/search-results.html?q=',
      logo: 'https://www.albertsons.com/content/dam/albertsons/logos/albertsons-logo.svg',
      country: 'US',
      type: 'supermarket'
    },
    {
      id: 'publix',
      name: 'Publix',
      searchUrl: 'https://www.publix.com/search?searchTerm=',
      logo: 'https://www.publix.com/-/media/project/publix/publix-website/publix-logos/publix-logo.svg',
      country: 'US',
      type: 'supermarket'
    },
    {
      id: 'traderjoes',
      name: 'Trader Joe\'s',
      searchUrl: 'https://www.traderjoes.com/home/search?q=',
      logo: 'https://www.traderjoes.com/favicon.ico',
      country: 'US',
      type: 'supermarket'
    },
    {
      id: 'heb',
      name: 'H-E-B',
      searchUrl: 'https://www.heb.com/search?q=',
      logo: 'https://www.heb.com/favicon.ico',
      country: 'US',
      type: 'supermarket'
    },
    {
      id: 'wegmans',
      name: 'Wegmans',
      searchUrl: 'https://shop.wegmans.com/search?search_term=',
      logo: 'https://shop.wegmans.com/favicon.ico',
      country: 'US',
      type: 'supermarket'
    },
    {
      id: 'aldi',
      name: 'Aldi',
      searchUrl: 'https://new.aldi.us/results?q=',
      logo: 'https://new.aldi.us/favicon.ico',
      country: 'US',
      type: 'supermarket'
    },
    // Delivery Services (5)
    {
      id: 'instacart',
      name: 'Instacart',
      searchUrl: 'https://www.instacart.com/store/items/search/',
      logo: 'https://www.instacart.com/assets/beetstrap/brand/2022/instacart-logo-color-6678cb82d531f8910d5ba270a11a7e9b56fc261371bda42ea7a5abeff3492e1c.svg',
      country: 'US',
      type: 'delivery'
    },
    {
      id: 'amazon-fresh',
      name: 'Amazon Fresh',
      searchUrl: 'https://www.amazon.com/s?i=amazonfresh&k=',
      logo: 'https://m.media-amazon.com/images/G/01/AmazonFresh/y2019/gw/marketing/AmazonFresh_Logo.png',
      country: 'US',
      type: 'delivery'
    },
    {
      id: 'freshdirect',
      name: 'FreshDirect',
      searchUrl: 'https://www.freshdirect.com/srch.jsp?searchParams=',
      logo: 'https://www.freshdirect.com/media/images/fd_logo.png',
      country: 'US',
      type: 'delivery'
    },
    {
      id: 'shipt',
      name: 'Shipt',
      searchUrl: 'https://www.shipt.com/search/',
      logo: 'https://www.shipt.com/wp-content/uploads/2020/04/shipt-logo.svg',
      country: 'US',
      type: 'delivery'
    },
    {
      id: 'gopuff',
      name: 'Gopuff',
      searchUrl: 'https://www.gopuff.com/search/',
      logo: 'https://www.gopuff.com/img/gopuff-logo.svg',
      country: 'US',
      type: 'delivery'
    }
  ],
  'UK': [
    // Supermarkets
    {
      id: 'tesco',
      name: 'Tesco',
      searchUrl: 'https://www.tesco.com/groceries/en-GB/search?query=',
      logo: 'https://www.tesco.com/groceries/assets/images/header/tesco-logo.svg',
      country: 'UK',
      type: 'supermarket'
    },
    {
      id: 'sainsburys',
      name: 'Sainsbury\'s',
      searchUrl: 'https://www.sainsburys.co.uk/gol-ui/SearchResults/',
      logo: 'https://www.sainsburys.co.uk/assets/images/sainsburys-logo.svg',
      country: 'UK',
      type: 'supermarket'
    },
    {
      id: 'asda',
      name: 'ASDA',
      searchUrl: 'https://groceries.asda.com/search/',
      logo: 'https://groceries.asda.com/assets/images/asdaLogo.svg',
      country: 'UK',
      type: 'supermarket'
    },
    {
      id: 'waitrose',
      name: 'Waitrose',
      searchUrl: 'https://www.waitrose.com/ecom/shop/search?&searchTerm=',
      logo: 'https://www.waitrose.com/content/dam/waitrose/logos/waitrose-logo.svg',
      country: 'UK',
      type: 'supermarket'
    },
    {
      id: 'marksandspencer',
      name: 'Marks & Spencer',
      searchUrl: 'https://www.marksandspencer.com/MSFindItemsByKeyword?searchTerm=',
      logo: 'https://asset1.marksandspencer.com/is/image/mands/MS_LOGO_2023',
      country: 'UK',
      type: 'supermarket'
    },
    // Delivery Services
    {
      id: 'ocado',
      name: 'Ocado',
      searchUrl: 'https://www.ocado.com/search?entry=',
      logo: 'https://www.ocado.com/cmscontent/ocado_logo_new.svg',
      country: 'UK',
      type: 'delivery'
    },
    {
      id: 'amazon-fresh-uk',
      name: 'Amazon Fresh',
      searchUrl: 'https://www.amazon.co.uk/s?i=amazonfresh&k=',
      logo: 'https://m.media-amazon.com/images/G/01/AmazonFresh/y2019/gw/marketing/AmazonFresh_Logo.png',
      country: 'UK',
      type: 'delivery'
    },
    {
      id: 'beelivery',
      name: 'Beelivery',
      searchUrl: 'https://www.beelivery.com/search?term=',
      logo: 'https://www.beelivery.com/images/beelivery-logo.svg',
      country: 'UK',
      type: 'delivery'
    },
    {
      id: 'gorillas',
      name: 'Gorillas',
      searchUrl: 'https://gorillas.io/en-gb/search?q=',
      logo: 'https://gorillas.io/images/gorillas-logo.svg',
      country: 'UK',
      type: 'delivery'
    },
    {
      id: 'getir',
      name: 'Getir',
      searchUrl: 'https://getir.com/search?q=',
      logo: 'https://getir.com/_next/static/images/getir-logo.svg',
      country: 'UK',
      type: 'delivery'
    }
  ],
  'CA': [
    // Supermarkets
    {
      id: 'loblaws',
      name: 'Loblaws',
      searchUrl: 'https://www.loblaws.ca/search?search-bar=',
      logo: 'https://www.loblaws.ca/content/dam/loblaws/logos/loblaws-logo.svg',
      country: 'CA',
      type: 'supermarket'
    },
    {
      id: 'metro',
      name: 'Metro',
      searchUrl: 'https://www.metro.ca/en/search?filter=',
      logo: 'https://www.metro.ca/favicon.ico',
      country: 'CA',
      type: 'supermarket'
    },
    {
      id: 'sobeys',
      name: 'Sobeys',
      searchUrl: 'https://www.sobeys.com/en/search-results?q=',
      logo: 'https://www.sobeys.com/wp-content/uploads/2019/07/sobeys-logo.png',
      country: 'CA',
      type: 'supermarket'
    },
    {
      id: 'saveonfoods',
      name: 'Save-On-Foods',
      searchUrl: 'https://www.saveonfoods.com/sm/pickup/rsid/987/results?q=',
      logo: 'https://www.saveonfoods.com/wp-content/uploads/2019/05/save-on-foods-logo.png',
      country: 'CA',
      type: 'supermarket'
    },
    {
      id: 'nofrills',
      name: 'No Frills',
      searchUrl: 'https://www.nofrills.ca/search?search-bar=',
      logo: 'https://www.nofrills.ca/favicon.ico',
      country: 'CA',
      type: 'supermarket'
    },
    // Delivery Services
    {
      id: 'instacart-ca',
      name: 'Instacart',
      searchUrl: 'https://www.instacart.ca/store/items/search/',
      logo: 'https://www.instacart.com/assets/beetstrap/brand/2022/instacart-logo-color-6678cb82d531f8910d5ba270a11a7e9b56fc261371bda42ea7a5abeff3492e1c.svg',
      country: 'CA',
      type: 'delivery'
    },
    {
      id: 'voila',
      name: 'Voilà by Sobeys',
      searchUrl: 'https://voila.ca/search?q=',
      logo: 'https://www.voila.ca/assets/images/voila-logo.svg',
      country: 'CA',
      type: 'delivery'
    },
    {
      id: 'cornershop',
      name: 'Cornershop',
      searchUrl: 'https://cornershopapp.com/en-ca/search?query=',
      logo: 'https://cornershopapp.com/assets/images/cornershop-logo.svg',
      country: 'CA',
      type: 'delivery'
    },
    {
      id: 'inabuggy',
      name: 'Inabuggy',
      searchUrl: 'https://www.inabuggy.com/Search?q=',
      logo: 'https://www.inabuggy.com/img/inabuggy-logo.png',
      country: 'CA',
      type: 'delivery'
    },
    {
      id: 'spud',
      name: 'SPUD.ca',
      searchUrl: 'https://www.spud.ca/catalogue/search.cfm?q=',
      logo: 'https://www.spud.ca/images/spud-logo.png',
      country: 'CA',
      type: 'delivery'
    }
  ],
  'AU': [
    // Supermarkets
    {
      id: 'woolworths',
      name: 'Woolworths',
      searchUrl: 'https://www.woolworths.com.au/shop/search/products?searchTerm=',
      logo: 'https://www.woolworths.com.au/favicon.ico',
      country: 'AU',
      type: 'supermarket'
    },
    {
      id: 'coles',
      name: 'Coles',
      searchUrl: 'https://shop.coles.com.au/a/national/everything/search/',
      logo: 'https://shop.coles.com.au/favicon.ico',
      country: 'AU',
      type: 'supermarket'
    },
    {
      id: 'aldi-au',
      name: 'ALDI Australia',
      searchUrl: 'https://www.aldi.com.au/en/search/?text=',
      logo: 'https://www.aldi.com.au/favicon.ico',
      country: 'AU',
      type: 'supermarket'
    },
    {
      id: 'iga',
      name: 'IGA',
      searchUrl: 'https://www.iga.com.au/catalogue/?s=',
      logo: 'https://www.iga.com.au/favicon.ico',
      country: 'AU',
      type: 'supermarket'
    },
    {
      id: 'harrisfarm',
      name: 'Harris Farm Markets',
      searchUrl: 'https://www.harrisfarm.com.au/search?q=',
      logo: 'https://www.harrisfarm.com.au/favicon.ico',
      country: 'AU',
      type: 'supermarket'
    },
    // Delivery Services
    {
      id: 'woolworths-delivery',
      name: 'Woolworths Delivery',
      searchUrl: 'https://www.woolworths.com.au/shop/search/products?searchTerm=',
      logo: 'https://www.woolworths.com.au/favicon.ico',
      country: 'AU',
      type: 'delivery'
    },
    {
      id: 'coles-online',
      name: 'Coles Online',
      searchUrl: 'https://shop.coles.com.au/a/national/everything/search/',
      logo: 'https://shop.coles.com.au/favicon.ico',
      country: 'AU',
      type: 'delivery'
    },
    {
      id: 'amazon-au',
      name: 'Amazon Australia',
      searchUrl: 'https://www.amazon.com.au/s?k=',
      logo: 'https://www.amazon.com.au/favicon.ico',
      country: 'AU',
      type: 'delivery'
    },
    {
      id: 'hellofresh-au',
      name: 'HelloFresh Australia',
      searchUrl: 'https://www.hellofresh.com.au/search?q=',
      logo: 'https://www.hellofresh.com.au/img/hellofresh-logo.svg',
      country: 'AU',
      type: 'delivery'
    },
    {
      id: 'marley-spoon-au',
      name: 'Marley Spoon Australia',
      searchUrl: 'https://marleyspoon.com.au/search?q=',
      logo: 'https://marleyspoon.com.au/images/logo.svg',
      country: 'AU',
      type: 'delivery'
    }
  ]
};